/**
 * Admin-area design tokens — Material 3 / Linear / Notion 2026 conventions.
 *
 * Goals:
 *  - Monochrome surface (white / gray) with accent only on actionable states.
 *  - Compact information density (admins are power users).
 *  - Consistent radii, type scale, motion across all admin pages.
 */

export const ADMIN_TYPE = {
  // Page-level
  pageTitle:
    'text-2xl font-semibold leading-tight tracking-tight sm:text-3xl',
  sectionTitle: 'text-lg font-semibold leading-snug tracking-tight',
  // Card / panel
  cardTitle: 'text-base font-semibold leading-6 tracking-tight',
  cardSubtitle: 'text-sm text-gray-500',
  // Body
  body: 'text-sm leading-5',
  bodySm: 'text-xs leading-4',
  // Label / overline
  label: 'text-sm font-medium leading-5',
  labelSm: 'text-xs font-medium leading-4',
  overline:
    'text-[11px] font-semibold uppercase tracking-[0.08em] leading-4',
  // Stat — large numerals for dashboard tiles
  statValue:
    'text-3xl font-semibold leading-none tracking-tight tabular-nums sm:text-4xl',
  statLabel:
    'text-[11px] font-semibold uppercase tracking-[0.08em] leading-4 text-gray-500',
} as const;

export const ADMIN_RADII = {
  surface: 'rounded-2xl',
  card: 'rounded-xl',
  control: 'rounded-lg',
  pill: 'rounded-full',
  field: 'rounded-lg',
} as const;

export const ADMIN_SPACING = {
  /** Page content padding — mobile-first. */
  page: 'px-4 py-6 sm:px-6 lg:px-8 lg:py-8',
  /** Inner card padding. */
  card: 'p-4 sm:p-5',
  cardLg: 'p-5 sm:p-6',
  /** Gap between major page sections. */
  sectionGap: 'space-y-6 lg:space-y-8',
} as const;

export const ADMIN_TOUCH = {
  /** 40px nav-row target. */
  navRow: 'min-h-[40px]',
  /** 44px touch target for icon buttons / chips. */
  control: 'min-h-[44px]',
} as const;

/**
 * Brand accent helper. Admin uses blue-600 (matches EazyBooking logo)
 * sparingly — only for active nav / primary CTAs / focus rings.
 */
export const ADMIN_ACCENT = {
  bg: 'bg-blue-600',
  bgHover: 'hover:bg-blue-700',
  text: 'text-blue-600',
  textHover: 'hover:text-blue-700',
  ring: 'focus-visible:ring-blue-600',
  tint: 'bg-blue-50',
  tintBorder: 'border-blue-200',
  tintText: 'text-blue-700',
} as const;
